
import React, { useState, useRef, useEffect } from 'react';

function ChatInput({ onSendMessage, conversationId }) {
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  
  // 🔥 Nuevos estados para el audio
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  
  const textInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    textInputRef.current?.focus();
    setSelectedFile(null);
  }, [conversationId]);

  // --- LÓGICA DE ARCHIVOS NORMALES ---
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      textInputRef.current?.focus();
    }
  };

  const getMessageType = (file) => {
    if (!file) return 'text';
    if (file.type.startsWith('image/')) return 'image';
    if (file.type.startsWith('video/')) return 'video';
    if (file.type.startsWith('audio/')) return 'audio';
    return 'document'; 
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (text.trim() || selectedFile) {
      const messageType = getMessageType(selectedFile);
      onSendMessage(text, selectedFile, messageType);
      
      setText('');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // --- 🔥 NUEVA LÓGICA DE GRABACIÓN DE AUDIO ---
  const startRecording = async () => {
    try {
      // Pide permiso al usuario para usar el micrófono
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        // Al detener, creamos el archivo de audio
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' }); // O audio/ogg
        
        // Lo convertimos en un objeto File igual al que genera el input de archivos
        const audioFile = new File([audioBlob], `nota_voz_${Date.now()}.webm`, { type: 'audio/webm' });
        
        // Lo enviamos INMEDIATAMENTE (sin texto extra, tipo 'audio')
        onSendMessage('', audioFile, 'audio');
        
        // Apagamos la luz roja del micrófono del navegador
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error al acceder al micrófono:", error);
      alert("No se pudo acceder al micrófono. Verifica los permisos de tu navegador.");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      // Para cancelar, detenemos el stream pero evitamos enviar el mensaje
      mediaRecorderRef.current.onstop = null; // Anulamos el evento onstop
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
    }
  };

  return (
    <div className="chat-input-wrapper" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      
      {/* PREVISUALIZACIÓN DE ARCHIVO ADJUNTO */}
      {selectedFile && !isRecording && (
        <div style={{ padding: '8px 12px', backgroundColor: '#e2e8f0', borderRadius: '8px 8px 0 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '13px' }}>
          <span>📎 {selectedFile.name}</span>
          <button 
            type="button" 
            onClick={() => { setSelectedFile(null); fileInputRef.current.value = ''; }}
            style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer', fontWeight: 'bold' }}
          >
            ✕ Quitar
          </button>
        </div>
      )}

      {/* FORMULARIO PRINCIPAL */}
      <form 
        className="chat-input-form" 
        onSubmit={handleSubmit}
        style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '10px', backgroundColor: '#f8fafc', borderTop: '1px solid #cbd5e1' }}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileSelect}
          accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx"
        />

        {/* 🌟 INTERFAZ DE GRABACIÓN VS INTERFAZ NORMAL */}
        {isRecording ? (
          // Vista mientras se graba audio
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', color: '#ef4444', fontWeight: 'bold', animation: 'pulse 1.5s infinite' }}>
              <span style={{ height: '10px', width: '10px', backgroundColor: '#ef4444', borderRadius: '50%', display: 'inline-block' }}></span>
              Grabando audio...
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" onClick={cancelRecording} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: '20px' }} title="Cancelar">
                🗑️
              </button>
              <button type="button" onClick={stopRecording} style={{ background: '#ef4444', border: 'none', color: 'white', borderRadius: '50%', width: '35px', height: '35px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} title="Enviar nota de voz">
                ⬆️
              </button>
            </div>
          </div>
        ) : (
          // Vista normal (Texto y Clip)
          <>
            <button 
              type="button" 
              onClick={() => fileInputRef.current?.click()}
              style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', padding: '5px' }}
              title="Adjuntar archivo"
            >
              📎
            </button>

            <input
              type="text"
              ref={textInputRef}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={selectedFile ? "Añade un comentario (opcional)..." : "Escribe un mensaje..."}
              style={{ flex: 1, padding: '10px', borderRadius: '20px', border: '1px solid #cbd5e1', outline: 'none' }}
            />

            {/* Si no hay texto ni archivos, mostramos el micrófono. Si hay texto, mostramos Enviar */}
            {text.trim() || selectedFile ? (
              <button 
                type="submit"
                style={{ padding: '10px 20px', backgroundColor: '#2563eb', color: 'white', border: 'none', borderRadius: '20px', cursor: 'pointer' }}
              >
                Enviar
              </button>
            ) : (
              <button 
                type="button"
                onClick={startRecording}
                style={{ padding: '10px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '50%', width: '40px', height: '40px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}
                title="Grabar nota de voz"
              >
                🎤
              </button>
            )}
          </>
        )}
      </form>

      {/* Estilo para el parpadeo del botón de grabar */}
      <style>{`
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.5; }
          100% { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default ChatInput;
